import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

import {
  AudioRecordPage,
  AudioRecordData,
} from '@karehero/llama/pwa/pages/AudioRecordPage/AudioRecordPage';

import { isSystemTag } from '@karehero/models';
import { useGoBack } from 'hooks/useGoBack';
import {
  MAX_RECORDING_DURATION_SECONDS,
  useAudioRecorder,
} from 'hooks/useAudioRecorder';
import { useDocumentUpload } from 'hooks/useDocumentUpload';
import { useTracking, TrackingEvent } from 'hooks/useTracking';

import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { newToast } from 'store/slices/uiSlice';
import { useListOrganiseDocumentTagsQuery } from 'store/api/organiseDocument';

export const AudioRecord = () => {
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const { track } = useTracking();
  const careCircle = useSelector(getCurrentCareCircle);
  const careCircleId = careCircle?.id ?? '';

  const [isUploading, setIsUploading] = useState(false);

  const recorder = useAudioRecorder();
  const { uploadDocument } = useDocumentUpload(careCircleId);

  const { data: allTags = [] } = useListOrganiseDocumentTagsQuery(
    careCircleId,
    { skip: !careCircleId },
  );
  const tags = allTags.filter((tag) => !isSystemTag(tag.id));

  useEffect(() => {
    if (
      recorder.state === 'recording' &&
      recorder.durationSeconds >= MAX_RECORDING_DURATION_SECONDS
    ) {
      recorder.stop();
    }
  }, [recorder]);

  const handleStartRecording = useCallback(async () => {
    track(TrackingEvent.AudioRecordingStarted, { careCircleId });
    await recorder.start();
  }, [recorder, track, careCircleId]);

  const handleStopRecording = useCallback(() => {
    track(TrackingEvent.AudioRecordingCompleted, {
      careCircleId,
      durationSeconds: recorder.durationSeconds,
    });
    recorder.stop();
  }, [recorder, track, careCircleId]);

  const handleUpload = useCallback(
    async (data: AudioRecordData) => {
      if (!careCircleId) return;

      setIsUploading(true);

      try {
        const namedFile = new File([data.file], data.title, {
          type: data.file.type,
        });
        await uploadDocument(namedFile);

        track(TrackingEvent.AudioRecordingUploaded, {
          careCircleId,
          contentType: data.file.type,
          sizeBytes: data.file.size,
        });

        dispatch(
          newToast({
            title: 'Recording uploaded',
            description: 'Your audio recording has been uploaded successfully.',
          }),
        );

        goBack();
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Upload failed',
            description:
              'An error occurred while uploading the recording. Please try again.',
          }),
        );
      } finally {
        setIsUploading(false);
      }
    },
    [careCircleId, uploadDocument, goBack, dispatch, track],
  );

  return (
    <AudioRecordPage
      recordingState={recorder.state}
      durationSeconds={recorder.durationSeconds}
      maxDurationSeconds={MAX_RECORDING_DURATION_SECONDS}
      recordingError={recorder.error}
      recordedFile={recorder.file}
      availableTags={tags}
      isUploading={isUploading}
      onBack={goBack}
      onStartRecording={handleStartRecording}
      onStopRecording={handleStopRecording}
      onResetRecording={recorder.reset}
      onUpload={handleUpload}
    />
  );
};

export default AudioRecord;
